import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { Trash2, ArrowRight } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { toast } from "sonner";

export default function Cart() {
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [checkoutId, setCheckoutId] = useState(null);

    const load = async () => {
        try {
            const d = await api.get("/cart");
            setData(d);
        } catch { setData({ items: [], total: 0 }); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        await api.del(`/cart/${id}`);
        toast.success("Removed from cart");
        load();
    };

    const checkout = async (courseId) => {
        setCheckoutId(courseId);
        try {
            const r = await api.post("/checkout/session", {
                course_id: courseId,
                origin_url: window.location.origin,
            });
            window.location.href = r.url;
        } catch (e) {
            toast.error("Could not start checkout");
            setCheckoutId(null);
        }
    };

    return (
        <AppLayout searchPlaceholder="Search...">
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-2">Cart</h1>
            <p className="text-sm text-gray-400 mb-8">{data.items.length} item{data.items.length !== 1 ? "s" : ""} • Total £{data.total.toFixed(2)}</p>

            {loading ? <div className="text-gray-500">Loading…</div> : data.items.length === 0 ? (
                <div className="bg-ink-700 rounded-3xl border border-white/5 p-10 text-center">
                    <p className="text-gray-400">Your cart is empty. <Link to="/catalog" className="text-lime font-semibold">Browse courses</Link>.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-3">
                        {data.items.map((c) => (
                            <div key={c.id} data-testid={`cart-item-${c.id}`} className="bg-ink-700 rounded-2xl border border-white/5 p-4 flex items-center gap-4">
                                <img src={c.thumbnail} alt={c.title} className="w-24 h-20 rounded-xl object-cover shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-grape font-semibold">{c.category}</div>
                                    <Link to={`/catalog/${c.id}`} className="font-display font-bold leading-tight hover:text-lime line-clamp-2">{c.title}</Link>
                                    <div className="text-xs text-gray-500 mt-1">{c.duration_hours || c.durationHours}h · {c.level}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display font-black text-xl">${c.price}</div>
                                    <button onClick={() => remove(c.id)} className="mt-2 text-gray-500 hover:text-red-400 transition-colors">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="bg-ink-700 rounded-3xl border border-white/5 p-6 h-max">
                        <h3 className="font-display font-bold text-xl mb-5">Order summary</h3>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between"><span className="text-gray-400">Subtotal</span><span>£{data.total.toFixed(2)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-400">Discount</span><span className="text-lime">£0.00</span></div>
                            <div className="border-t border-white/5 pt-3 flex justify-between font-display font-bold text-lg">
                                <span>Total</span><span>£{data.total.toFixed(2)}</span>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-4">Checkout via Stripe runs each course as a separate session — click below to start with the first item.</p>
                        <button
                            disabled={!data.items.length || !!checkoutId}
                            onClick={() => checkout(data.items[0].id)}
                            data-testid="cart-checkout"
                            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-lime text-ink-900 font-bold disabled:opacity-50 hover:bg-lime-hover transition-all"
                        >
                            {checkoutId ? "Redirecting…" : "Checkout"} <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </AppLayout>
    );
}
